import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Coins, Download, FileSpreadsheet, Plus, RefreshCw, Upload, Users } from "lucide-react";

import api, { getApiErrorMessage } from "../../api";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: Coins },
  { id: "customers", label: "Customers", icon: Users },
  { id: "create", label: "Create Receipt", icon: Plus },
  { id: "pending", label: "Pending Approval", icon: CheckCircle2 },
  { id: "import", label: "Historical Import", icon: Upload },
];

const BLANK_ITEM = {
  lot_detail: "",
  pieces: "0",
  gross_weight_grams: "",
  less_weight_grams: "",
  net_weight_grams: "",
  wastage_percentage: "",
  labor_rate_code: "4/G",
  purity_pct: "92.5",
  silver_content_grams: "",
  amount_rupees: "",
};

function emptyTransactionForm(customerId = "") {
  return {
    customer_id: customerId,
    transaction_type: "MIXED",
    metal_direction: "BORROW",
    metal_type: "SILVER",
    quantity_value: "",
    quantity_unit: "G",
    rate_per_g: "",
    cash_payment_rupees: "",
    cash_receipt_rupees: "",
    due_date: "",
    deadline_date: "",
    notes: "",
    items: [{ ...BLANK_ITEM }],
  };
}

function formatMoney(value) {
  return `Rs. ${Number(value || 0).toFixed(2)}`;
}

function formatGrams(value) {
  return `${Number(value || 0).toFixed(3)} g`;
}

function quantityToGrams(value, unit) {
  const amount = Number(value || 0);
  return (unit || "G") === "KG" ? amount * 1000 : amount;
}

function inputClassName(extra = "") {
  return `w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 ${extra}`.trim();
}

function normalizeItems(items) {
  return items.map((item) => {
    const gross = Number(item.gross_weight_grams || 0);
    const less = Number(item.less_weight_grams || 0);
    const net = Number(item.net_weight_grams || 0) || Math.max(0, gross - less);
    const purity = Number(item.purity_pct || 92.5) / 100;
    const silver = Number(item.silver_content_grams || 0) || Number((net * purity).toFixed(4));
    const laborCode = item.labor_rate_code || "";
    let amount = Number(item.amount_rupees || 0);
    if (!amount && laborCode.includes("/")) {
      const [rate, unit] = laborCode.toUpperCase().split("/");
      const parsedRate = Number(rate || 0);
      if (parsedRate > 0) {
        const basis = unit === "P" ? Number(item.pieces || 0) : silver;
        amount = Number((basis * parsedRate).toFixed(2));
      }
    }
    return {
      ...item,
      pieces: Number(item.pieces || 0),
      gross_weight_grams: gross,
      less_weight_grams: less,
      net_weight_grams: net,
      purity_pct: Number(item.purity_pct || 92.5),
      silver_content_grams: silver,
      amount_rupees: amount,
    };
  });
}

function summarizeItems(items) {
  const rows = normalizeItems(items);
  return rows.reduce(
    (acc, item) => ({
      pieces: acc.pieces + Number(item.pieces || 0),
      gross: acc.gross + Number(item.gross_weight_grams || 0),
      less: acc.less + Number(item.less_weight_grams || 0),
      net: acc.net + Number(item.net_weight_grams || 0),
      silver: acc.silver + Number(item.silver_content_grams || 0),
      amount: acc.amount + Number(item.amount_rupees || 0),
    }),
    { pieces: 0, gross: 0, less: 0, net: 0, silver: 0, amount: 0 }
  );
}

function MetalExchangeList() {
  const [tab, setTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [customers, setCustomers] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [pendingRows, setPendingRows] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedCustomerDetail, setSelectedCustomerDetail] = useState(null);
  const [statementRows, setStatementRows] = useState([]);
  const [statementSummary, setStatementSummary] = useState(null);
  const [statementDateFrom, setStatementDateFrom] = useState("");
  const [statementDateTo, setStatementDateTo] = useState("");
  const [marketRate, setMarketRate] = useState("");
  const [csvPreview, setCsvPreview] = useState([]);
  const [transactionForm, setTransactionForm] = useState(emptyTransactionForm());

  const selectedCustomer = useMemo(
    () => customers.find((customer) => String(customer.id) === String(selectedCustomerId || transactionForm.customer_id)) || null,
    [customers, selectedCustomerId, transactionForm.customer_id]
  );
  const itemSummary = useMemo(() => summarizeItems(transactionForm.items), [transactionForm.items]);
  const isSimpleMetalTransaction = ["BORROW_METAL", "RETURN_METAL"].includes(transactionForm.transaction_type);
  const isCashSettlementTransaction = transactionForm.transaction_type === "SETTLE_BY_CASH";
  const showItemTable = transactionForm.transaction_type === "MIXED";
  const quantityInGrams = quantityToGrams(transactionForm.quantity_value, transactionForm.quantity_unit);
  const settlementMetalCut = isCashSettlementTransaction && Number(transactionForm.rate_per_g || marketRate || 0) > 0
    ? Number(transactionForm.cash_payment_rupees || 0) / Number(transactionForm.rate_per_g || marketRate || 0)
    : 0;
  const projectedMetalChange = isCashSettlementTransaction
    ? -settlementMetalCut
    : transactionForm.transaction_type === "RETURN_METAL" || (transactionForm.transaction_type === "MIXED" && transactionForm.metal_direction === "RETURN")
      ? -(showItemTable ? itemSummary.silver : quantityInGrams)
      : (showItemTable ? itemSummary.silver : quantityInGrams);
  const projectedCashChange = transactionForm.transaction_type === "CASH_PAYMENT"
    ? -Number(transactionForm.cash_payment_rupees || 0)
    : transactionForm.transaction_type === "SETTLE_BY_CASH"
      ? -Number(transactionForm.cash_payment_rupees || 0)
    : transactionForm.transaction_type === "CASH_RECEIPT"
      ? Number(transactionForm.cash_receipt_rupees || 0)
      : transactionForm.transaction_type === "MIXED"
        ? itemSummary.amount + Number(transactionForm.cash_receipt_rupees || 0) - Number(transactionForm.cash_payment_rupees || 0)
        : 0;
  const isMetalTransaction = ["BORROW_METAL", "RETURN_METAL", "SETTLE_BY_CASH", "MIXED"].includes(transactionForm.transaction_type);
  const showMetalDirection = transactionForm.transaction_type === "MIXED";
  const showCashPayment = ["CASH_PAYMENT", "SETTLE_BY_CASH", "MIXED"].includes(transactionForm.transaction_type);
  const showCashReceipt = ["CASH_RECEIPT", "MIXED"].includes(transactionForm.transaction_type);
  const showDueFields = ["BORROW_METAL", "RETURN_METAL", "SETTLE_BY_CASH", "MIXED"].includes(transactionForm.transaction_type);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [customerRes, dashboardRes, pendingRes, rateRes] = await Promise.all([
        api.get("/metal-exchange/customers"),
        api.get("/metal-exchange/dashboard/stats"),
        api.get("/metal-exchange/approval-requests?status=PENDING"),
        api.get("/metal-exchange/market-rates?metal_type=SILVER"),
      ]);
      const customerRows = customerRes.data?.data || [];
      setCustomers(customerRows);
      setDashboard(dashboardRes.data || {});
      setPendingRows(pendingRes.data?.data || []);
      setMarketRate(String(rateRes.data?.latest?.rate_per_gram || ""));
      setTransactionForm((prev) => ({
        ...prev,
        customer_id: prev.customer_id || String(customerRows[0]?.id || ""),
        rate_per_g: prev.rate_per_g || String(rateRes.data?.latest?.rate_per_gram || ""),
      }));
      setSelectedCustomerId((prev) => prev || String(customerRows[0]?.id || ""));
      setError("");
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load metal exchange workspace."));
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerStatement = async (customerId) => {
    if (!customerId) return;
    try {
      const params = {};
      if (statementDateFrom) params.date_from = statementDateFrom;
      if (statementDateTo) params.date_to = statementDateTo;
      const [detailRes, statementRes] = await Promise.all([
        api.get(`/metal-exchange/customers/${customerId}`),
        api.get(`/metal-exchange/customers/${customerId}/statement`, { params }),
      ]);
      setSelectedCustomerDetail(detailRes.data?.customer || null);
      setStatementRows(statementRes.data?.data || []);
      setStatementSummary(statementRes.data?.summary || null);
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load customer statement."));
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      loadCustomerStatement(selectedCustomerId);
    }
  }, [selectedCustomerId, statementDateFrom, statementDateTo]);

  const updateItem = (index, field, value) => {
    setTransactionForm((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
    }));
  };

  const addItem = () => setTransactionForm((prev) => ({ ...prev, items: [...prev.items, { ...BLANK_ITEM }] }));
  const removeItem = (index) => setTransactionForm((prev) => ({ ...prev, items: prev.items.filter((_, itemIndex) => itemIndex !== index) || [{ ...BLANK_ITEM }] }));

  const handleTransactionTypeChange = (nextType) => {
    setTransactionForm((prev) => {
      const next = { ...prev, transaction_type: nextType };
      if (["BORROW_METAL", "RETURN_METAL"].includes(nextType)) {
        next.metal_direction = nextType === "RETURN_METAL" ? "RETURN" : "BORROW";
        next.cash_payment_rupees = "";
        next.cash_receipt_rupees = "";
      } else if (nextType === "SETTLE_BY_CASH") {
        next.metal_direction = "RETURN";
        next.quantity_value = "";
        next.quantity_unit = "G";
        next.items = [{ ...BLANK_ITEM }];
        next.cash_receipt_rupees = "";
      } else if (nextType === "CASH_PAYMENT") {
        next.metal_direction = "BORROW";
        next.quantity_value = "";
        next.quantity_unit = "G";
        next.items = [{ ...BLANK_ITEM }];
        next.cash_receipt_rupees = "";
      } else if (nextType === "CASH_RECEIPT") {
        next.metal_direction = "BORROW";
        next.quantity_value = "";
        next.quantity_unit = "G";
        next.items = [{ ...BLANK_ITEM }];
        next.cash_payment_rupees = "";
      } else {
        next.quantity_value = "";
        next.quantity_unit = "G";
      }
      return next;
    });
  };

  const submitTransaction = async () => {
    if (!transactionForm.customer_id) {
      setError("Choose a customer before saving the receipt.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...transactionForm,
        customer_id: Number(transactionForm.customer_id),
        rate_per_g: Number(transactionForm.rate_per_g || marketRate || 0),
        quantity_value: isSimpleMetalTransaction ? Number(transactionForm.quantity_value || 0) : 0,
        quantity_unit: isSimpleMetalTransaction ? transactionForm.quantity_unit : "G",
        cash_payment_rupees: showCashPayment ? Number(transactionForm.cash_payment_rupees || 0) : 0,
        cash_receipt_rupees: showCashReceipt ? Number(transactionForm.cash_receipt_rupees || 0) : 0,
        due_date: showDueFields ? transactionForm.due_date : "",
        deadline_date: showDueFields ? transactionForm.deadline_date : "",
        items: showItemTable ? normalizeItems(transactionForm.items) : [],
      };
      const res = await api.post("/metal-exchange/transactions", payload);
      setMessage(`Receipt ${res.data?.transaction?.transaction_no} saved.`);
      setTransactionForm(emptyTransactionForm(transactionForm.customer_id));
      setTransactionForm((prev) => ({ ...prev, customer_id: payload.customer_id.toString(), rate_per_g: String(payload.rate_per_g || marketRate || "") }));
      await loadAll();
      await loadCustomerStatement(payload.customer_id);
      setTab("customers");
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to save transaction."));
    } finally {
      setSaving(false);
    }
  };

  const actOnPending = async (row, action) => {
    setSaving(true);
    try {
      if (action === "approve") {
        await api.post(`/metal-exchange/transactions/${row.id}/approve`);
      } else {
        await api.post(`/metal-exchange/transactions/${row.id}/reject`, { rejection_reason: "Rejected from accountant dashboard" });
      }
      await loadAll();
      if (selectedCustomerId) {
        await loadCustomerStatement(selectedCustomerId);
      }
      setMessage(`Transaction ${row.transaction_no} ${action}d.`);
    } catch (err) {
      setError(getApiErrorMessage(err, `Failed to ${action} transaction.`));
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (transactionId, transactionNo) => {
    try {
      const res = await api.get(`/metal-exchange/transactions/${transactionId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${transactionNo || "metal-exchange"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to download PDF receipt."));
    }
  };

  const downloadStatementPdf = async (customerId, customerCode) => {
    try {
      const params = {};
      if (statementDateFrom) params.date_from = statementDateFrom;
      if (statementDateTo) params.date_to = statementDateTo;
      const res = await api.get(`/metal-exchange/customers/${customerId}/statement/pdf`, { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${customerCode || customerId}-metal-exchange-statement.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to download customer statement PDF."));
    }
  };

  const onCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const [headerLine, ...bodyLines] = text.split(/\r?\n/).filter(Boolean);
    const headers = headerLine.split(",").map((part) => part.trim());
    const rows = bodyLines.map((line) => {
      const columns = line.split(",").map((part) => part.trim());
      const row = Object.fromEntries(headers.map((header, index) => [header, columns[index] || ""]));
      return {
        customer_id: Number(row.customer_id),
        transaction_date: row.transaction_date,
        transaction_type: row.transaction_type || "MIXED",
        metal_direction: row.metal_direction || "BORROW",
        cash_payment_rupees: Number(row.cash_payment_rupees || 0),
        cash_receipt_rupees: Number(row.cash_receipt_rupees || 0),
        notes: row.notes || "Imported historical row",
        items: [{
          lot_detail: row.lot_detail || "Imported item",
          pieces: Number(row.pieces || 0),
          gross_weight_grams: Number(row.gross_weight_grams || 0),
          less_weight_grams: Number(row.less_weight_grams || 0),
          purity_pct: Number(row.purity_pct || 92.5),
          labor_rate_code: row.labor_rate_code || "0/G",
          amount_rupees: Number(row.amount_rupees || 0),
        }],
      };
    });
    setCsvPreview(rows);
  };

  const importHistorical = async () => {
    if (!csvPreview.length) {
      setError("Upload a CSV file before importing.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/metal-exchange/transactions/import-historical", { transactions: csvPreview });
      setMessage(`Imported ${res.data?.created_count || 0} historical transactions.`);
      setCsvPreview([]);
      await loadAll();
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to import historical transactions."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}
      {message && <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</div>}

      <section className="rounded-[2rem] border border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#1d4ed8_55%,#0ea5e9_100%)] px-6 py-6 text-white shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-sky-100">Accountant Metal Exchange</div>
            <h2 className="mt-2 text-3xl font-bold">Receipt-driven metal and cash exchange workflow</h2>
            <p className="mt-2 max-w-3xl text-sm text-sky-50">Track metal borrowing, metal cash adjustments, pending approvals, and pure metal-exchange customer statements from one accountant workspace.</p>
          </div>
          <button onClick={loadAll} className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <Kpi label="Customers" value={dashboard.total_customers || customers.length || 0} tone="indigo" />
        <Kpi label="Metal Outstanding" value={formatGrams(dashboard.total_metal_outstanding_g || 0)} tone="amber" />
        <Kpi label="Credit Outstanding" value={formatMoney(dashboard.total_credit_outstanding_rs || 0)} tone="emerald" />
        <Kpi label="Pending Approval" value={dashboard.pending_approval_count || pendingRows.length || 0} tone="rose" />
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <Kpi label="Customers Over Credit" value={dashboard.customers_over_credit_limit || 0} tone="rose" />
        <Kpi label="Overdue Credit" value={dashboard.overdue_credit_count || 0} tone="amber" />
        <Kpi label="Metal Cash Exposure" value={formatMoney(dashboard.total_cash_outstanding_rs || 0)} tone="indigo" />
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-2 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {TABS.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.id} type="button" onClick={() => setTab(item.id)} className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${tab === item.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <Icon size={15} /> {item.label}
              </button>
            );
          })}
        </div>
      </section>

      {tab === "dashboard" && (
        <section className="grid gap-4 lg:grid-cols-[1.1fr,0.9fr]">
          <Panel title="Customer Exposure">
            <div className="max-h-[420px] overflow-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Metal</th>
                    <th className="px-4 py-3">Credit</th>
                    <th className="px-4 py-3">Utilization</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((row) => (
                    <tr key={row.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-semibold text-slate-900">{row.name}<div className="text-xs text-slate-500">{row.code}</div></td>
                      <td className="px-4 py-3">{row.current_metal_balance_display}</td>
                      <td className="px-4 py-3">{formatMoney(row.outstanding_balance)}<div className="text-xs text-slate-500">{row.over_limit_status}</div></td>
                      <td className="px-4 py-3">{Math.max(Number(row.metal_utilization_pct || 0), Number(row.credit_utilization_pct || 0), Number(row.cash_utilization_pct || 0)).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
          <Panel title="Approval Queue">
            {!pendingRows.length && <div className="rounded-2xl bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">No transactions are waiting for approval.</div>}
            <div className="space-y-3">
              {pendingRows.slice(0, 6).map((row) => (
                <div key={row.id} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{row.transaction_no}</div>
                      <div className="text-xs text-slate-500">{row.customer_name} · {row.transaction_type}</div>
                    </div>
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">{row.approval_status}</span>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </section>
      )}

      {tab === "customers" && (
        <section className="grid gap-4 lg:grid-cols-[0.95fr,1.05fr]">
          <Panel title="Customer List">
            <div className="max-h-[560px] overflow-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Exposure</th><th className="px-4 py-3">Status</th></tr>
                </thead>
                <tbody>
                  {customers.map((row) => (
                    <tr key={row.id} className={`cursor-pointer border-t border-slate-100 ${String(selectedCustomerId) === String(row.id) ? "bg-sky-50" : ""}`} onClick={() => setSelectedCustomerId(String(row.id))}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{row.name}<div className="text-xs text-slate-500">{row.code}</div></td>
                      <td className="px-4 py-3"><div>{row.current_metal_balance_display}</div><div className="text-xs text-slate-500">{formatMoney(row.outstanding_balance)} / limit {formatMoney(row.credit_limit)}</div></td>
                      <td className="px-4 py-3"><div>{row.over_limit_status}</div><div className="text-xs text-slate-500">{row.overdue_status}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
          <Panel title={selectedCustomer ? `${selectedCustomer.name} Statement` : "Customer Statement"}>
            {selectedCustomerDetail && (
              <div className="mb-4 grid gap-3 md:grid-cols-4">
                <MiniStat label="Metal Balance" value={selectedCustomerDetail.current_metal_balance_display} />
                <MiniStat label="Metal Limit" value={formatGrams(selectedCustomerDetail.metal_credit_limit_grams)} />
                <MiniStat label="Credit Outstanding" value={formatMoney(selectedCustomerDetail.outstanding_balance)} />
                <MiniStat label="Credit Limit" value={formatMoney(selectedCustomerDetail.credit_limit)} />
              </div>
            )}
            {statementSummary && (
              <div className="mb-4 grid gap-3 md:grid-cols-3">
                <MiniStat label="Previous Balance" value={`${statementSummary.previous_balance?.metal_display || "-"} / ${statementSummary.previous_balance?.cash_display || "-"}`} />
                <MiniStat label="Net Total" value={`${statementSummary.net_total?.metal_display || "-"} / ${statementSummary.net_total?.cash_display || "-"}`} />
                <MiniStat label="Closing Balance" value={`${statementSummary.closing_balance?.metal_display || "-"} / ${statementSummary.closing_balance?.cash_display || "-"}`} />
              </div>
            )}
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="From"><input type="date" value={statementDateFrom} onChange={(event) => setStatementDateFrom(event.target.value)} className={inputClassName()} /></Field>
                <Field label="To"><input type="date" value={statementDateTo} onChange={(event) => setStatementDateTo(event.target.value)} className={inputClassName()} /></Field>
              </div>
              {selectedCustomerId && (
                <button onClick={() => downloadStatementPdf(selectedCustomerId, selectedCustomer?.code)} className="table-btn">
                  <Download size={14} /> Download Statement
                </button>
              )}
            </div>
            <div className="max-h-[520px] overflow-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="px-4 py-3">Reference</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Opening</th><th className="px-4 py-3">Movement</th><th className="px-4 py-3">Closing</th><th className="px-4 py-3">Due Cycle</th></tr>
                </thead>
                <tbody>
                  {statementRows.map((row, index) => (
                    <tr key={`${row.reference_no}-${row.date}-${index}`} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-semibold text-slate-900">{row.reference_no}<div className="text-xs text-slate-500">{String(row.date || "").slice(0, 10)}</div></td>
                      <td className="px-4 py-3">{row.transaction_type || row.source_type}<div className="text-xs text-slate-500">{row.approval_status}</div></td>
                      <td className="px-4 py-3"><div>{row.metal_balance_before_display}</div><div className="text-xs text-slate-500">{row.cash_balance_before_display}</div></td>
                      <td className="px-4 py-3"><div>{formatGrams(row.metal_delta_g || 0)}</div><div className="text-xs text-slate-500">{formatMoney(row.rupee_delta || 0)}{Number(row.rate_used_per_g || 0) > 0 ? ` @ ${formatMoney(row.rate_used_per_g)}/g` : ""}</div></td>
                      <td className="px-4 py-3"><div>{row.metal_balance_after_display}</div><div className="text-xs text-slate-500">{row.rupee_balance_after_display}</div></td>
                      <td className="px-4 py-3">{row.due_date || "-"}<div className="text-xs text-slate-500">{row.deadline_date || "-"} · {row.cycle_rating_result || row.overdue_status}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </section>
      )}

      {tab === "create" && (
        <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Customer"><select value={transactionForm.customer_id} onChange={(event) => setTransactionForm((prev) => ({ ...prev, customer_id: event.target.value }))} className={inputClassName()}>{customers.map((row) => <option key={row.id} value={row.id}>{row.name} ({row.code})</option>)}</select></Field>
            <Field label="Transaction Type"><select value={transactionForm.transaction_type} onChange={(event) => handleTransactionTypeChange(event.target.value)} className={inputClassName()}>{["MIXED", "BORROW_METAL", "RETURN_METAL", "SETTLE_BY_CASH", "CASH_PAYMENT", "CASH_RECEIPT"].map((value) => <option key={value} value={value}>{value}</option>)}</select></Field>
            {showMetalDirection ? (
              <Field label="Metal Direction"><select value={transactionForm.metal_direction} onChange={(event) => setTransactionForm((prev) => ({ ...prev, metal_direction: event.target.value }))} className={inputClassName()}><option value="BORROW">BORROW</option><option value="RETURN">RETURN</option></select></Field>
            ) : (
              <Field label="Flow"><div className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">{transactionForm.transaction_type === "RETURN_METAL" ? "Customer returns metal" : transactionForm.transaction_type === "BORROW_METAL" ? "Customer borrows metal" : transactionForm.transaction_type === "SETTLE_BY_CASH" ? "Customer pays money and equivalent metal is cut at the selected rate" : transactionForm.transaction_type === "CASH_PAYMENT" ? "Customer pays cash" : "Cash receipt to customer"}</div></Field>
            )}
            {isMetalTransaction && <Field label="Rate / gram"><input value={transactionForm.rate_per_g} onChange={(event) => setTransactionForm((prev) => ({ ...prev, rate_per_g: event.target.value }))} className={inputClassName()} /></Field>}
            {isSimpleMetalTransaction && <Field label="Quantity"><input value={transactionForm.quantity_value} onChange={(event) => setTransactionForm((prev) => ({ ...prev, quantity_value: event.target.value }))} className={inputClassName()} placeholder="Enter borrowed or returned quantity" /></Field>}
            {isSimpleMetalTransaction && <Field label="Unit"><select value={transactionForm.quantity_unit} onChange={(event) => setTransactionForm((prev) => ({ ...prev, quantity_unit: event.target.value }))} className={inputClassName()}><option value="G">Grams</option><option value="KG">Kilograms</option></select></Field>}
            {showCashPayment && <Field label={isCashSettlementTransaction ? "Settlement Amount" : "Cash Payment"}><input value={transactionForm.cash_payment_rupees} onChange={(event) => setTransactionForm((prev) => ({ ...prev, cash_payment_rupees: event.target.value }))} className={inputClassName()} /></Field>}
            {showCashReceipt && <Field label="Cash Receipt"><input value={transactionForm.cash_receipt_rupees} onChange={(event) => setTransactionForm((prev) => ({ ...prev, cash_receipt_rupees: event.target.value }))} className={inputClassName()} /></Field>}
            {showDueFields && <Field label="Due Date"><input type="date" value={transactionForm.due_date} onChange={(event) => setTransactionForm((prev) => ({ ...prev, due_date: event.target.value }))} className={inputClassName()} /></Field>}
            {showDueFields && <Field label="Deadline"><input type="date" value={transactionForm.deadline_date} onChange={(event) => setTransactionForm((prev) => ({ ...prev, deadline_date: event.target.value }))} className={inputClassName()} /></Field>}
            <Field label="Notes"><input value={transactionForm.notes} onChange={(event) => setTransactionForm((prev) => ({ ...prev, notes: event.target.value }))} className={inputClassName()} /></Field>
          </div>

          {showItemTable && (
            <div className="rounded-3xl border border-slate-200">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <div className="text-sm font-semibold text-slate-900">Receipt Items</div>
                <button type="button" onClick={addItem} className="secondary-btn"><Plus size={15} /> Add Row</button>
              </div>
              <div className="overflow-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr><th className="px-3 py-3">Lot Detail</th><th className="px-3 py-3">Pc</th><th className="px-3 py-3">Gross</th><th className="px-3 py-3">Less</th><th className="px-3 py-3">Purity</th><th className="px-3 py-3">Lbr</th><th className="px-3 py-3">Amount</th><th className="px-3 py-3" /></tr>
                  </thead>
                  <tbody>
                    {transactionForm.items.map((item, index) => (
                      <tr key={index} className="border-t border-slate-100">
                        <td className="px-3 py-2"><input value={item.lot_detail} onChange={(event) => updateItem(index, "lot_detail", event.target.value)} className={inputClassName()} /></td>
                        <td className="px-3 py-2"><input value={item.pieces} onChange={(event) => updateItem(index, "pieces", event.target.value)} className={inputClassName()} /></td>
                        <td className="px-3 py-2"><input value={item.gross_weight_grams} onChange={(event) => updateItem(index, "gross_weight_grams", event.target.value)} className={inputClassName()} /></td>
                        <td className="px-3 py-2"><input value={item.less_weight_grams} onChange={(event) => updateItem(index, "less_weight_grams", event.target.value)} className={inputClassName()} /></td>
                        <td className="px-3 py-2"><input value={item.purity_pct} onChange={(event) => updateItem(index, "purity_pct", event.target.value)} className={inputClassName()} /></td>
                        <td className="px-3 py-2"><input value={item.labor_rate_code} onChange={(event) => updateItem(index, "labor_rate_code", event.target.value)} className={inputClassName()} /></td>
                        <td className="px-3 py-2"><input value={item.amount_rupees} onChange={(event) => updateItem(index, "amount_rupees", event.target.value)} className={inputClassName()} /></td>
                        <td className="px-3 py-2">{transactionForm.items.length > 1 && <button type="button" onClick={() => removeItem(index)} className="text-sm font-semibold text-rose-600">Remove</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-4">
            {showItemTable && <MiniStat label="Total Silver" value={formatGrams(itemSummary.silver)} />}
            {showItemTable && <MiniStat label="Item Value" value={formatMoney(itemSummary.amount)} />}
            {isSimpleMetalTransaction && <MiniStat label="Metal Quantity" value={formatGrams(quantityInGrams)} />}
            {isCashSettlementTransaction && <MiniStat label="Metal Cut by Payment" value={formatGrams(settlementMetalCut)} />}
            <MiniStat label="Projected Metal Change" value={formatGrams(projectedMetalChange)} />
            <MiniStat label="Projected Cash Change" value={formatMoney(projectedCashChange)} />
          </div>

          <div className="flex justify-end">
            <button type="button" onClick={submitTransaction} disabled={saving} className="primary-btn"><FileSpreadsheet size={16} /> Save Receipt</button>
          </div>
        </section>
      )}

      {tab === "pending" && (
        <Panel title="Pending Approval Transactions">
          <div className="space-y-3">
            {!pendingRows.length && <div className="rounded-2xl bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">No pending approvals.</div>}
            {pendingRows.map((row) => (
              <div key={row.id} className="rounded-2xl border border-slate-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-base font-semibold text-slate-900">{row.transaction_no}</div>
                    <div className="text-sm text-slate-500">{row.customer_name} · {row.transaction_type}</div>
                    <div className="mt-2 text-xs text-slate-500">{row.metal_balance_after_display} · {row.cash_balance_after_display}</div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => actOnPending(row, "approve")} disabled={saving} className="primary-btn">Approve</button>
                    <button type="button" onClick={() => actOnPending(row, "reject")} disabled={saving} className="secondary-btn">Reject</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {tab === "import" && (
        <Panel title="Historical Import">
          <div className="grid gap-4 md:grid-cols-[1fr,auto] md:items-end">
            <Field label="Upload CSV">
              <input type="file" accept=".csv" onChange={onCsvUpload} className="input py-2" />
            </Field>
            <button type="button" onClick={importHistorical} disabled={saving || !csvPreview.length} className="primary-btn"><Upload size={16} /> Import</button>
          </div>
          <div className="mt-4 overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Item</th></tr>
              </thead>
              <tbody>
                {csvPreview.map((row, index) => (
                  <tr key={index} className="border-t border-slate-100">
                    <td className="px-4 py-3">{row.customer_id}</td>
                    <td className="px-4 py-3">{row.transaction_date}</td>
                    <td className="px-4 py-3">{row.transaction_type}</td>
                    <td className="px-4 py-3">{row.items?.[0]?.lot_detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}

function Panel({ title, children }) {
  return <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"><div className="mb-4 text-lg font-bold text-slate-900">{title}</div>{children}</section>;
}

function Field({ label, children }) {
  return <label className="space-y-1"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>{children}</label>;
}

function Kpi({ label, value, tone }) {
  const tones = {
    indigo: "from-indigo-100 to-indigo-50 text-indigo-700",
    amber: "from-amber-100 to-amber-50 text-amber-700",
    emerald: "from-emerald-100 to-emerald-50 text-emerald-700",
    rose: "from-rose-100 to-rose-50 text-rose-700",
  };
  return <div className={`rounded-3xl border border-slate-200 bg-gradient-to-br ${tones[tone]} p-5 shadow-sm`}><div className="text-xs font-semibold uppercase tracking-wide">{label}</div><div className="mt-3 text-2xl font-bold">{value}</div></div>;
}

function MiniStat({ label, value }) {
  return <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div><div className="mt-2 text-sm font-semibold text-slate-900">{value}</div></div>;
}

export default MetalExchangeList;
